import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Filter, Search, Eye, MapPin, X, ArrowUpRight } from "lucide-react";

import { api, formatINR, formatCompactNumber } from "@/lib/api";
import { IMG, imageForFormat } from "@/lib/images";
import SmartImage from "@/components/common/SmartImage";

const SORTS = [
  { value: "premium", label: "Premium first" },
  { value: "price_asc", label: "Price · low to high" },
  { value: "price_desc", label: "Price · high to low" },
  { value: "footfall", label: "Highest footfall" },
];

export default function Marketplace() {
  const [filterOpts, setFilterOpts] = useState({ cities: [], formats: [], categories: [] });
  const [filters, setFilters] = useState({
    q: "", city: "", format: "", format_category: "",
    is_premium: undefined, indoor: undefined, max_price: "",
  });
  const [sort, setSort] = useState("premium");
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    api.get("/public/inventory/filters").then((r) => setFilterOpts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 60 };
    Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== undefined) params[k] = v; });
    api.get("/public/inventory", { params })
      .then((r) => setData(r.data))
      .catch(() => setData({ total: 0, items: [] }))
      .finally(() => setLoading(false));
  }, [filters]);

  const sorted = useMemo(() => {
    const items = [...data.items];
    if (sort === "price_asc") items.sort((a, b) => a.price_per_day - b.price_per_day);
    if (sort === "price_desc") items.sort((a, b) => b.price_per_day - a.price_per_day);
    if (sort === "footfall") items.sort((a, b) => (b.daily_footfall || b.monthly_footfall) - (a.daily_footfall || a.monthly_footfall));
    return items;
  }, [data.items, sort]);

  const clearAll = () => setFilters({ q: "", city: "", format: "", format_category: "", is_premium: undefined, indoor: undefined, max_price: "" });

  return (
    <div data-testid="marketplace-page" className="bg-[var(--bone)]">
      {/* HEADER — editorial banner */}
      <section className="relative pt-28 lg:pt-36 pb-12 lg:pb-16 border-b border-[var(--border)] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08]">
          <img src={IMG.luxuryGradient} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="text-[11px] uppercase tracking-[0.3em] font-semibold text-[var(--gold)] flex items-center gap-3">
            <span className="inline-block w-8 h-px bg-[var(--gold)]" /> The Marketplace · Live
          </div>
          <h1 className="mt-5 font-serif-display text-5xl md:text-6xl lg:text-7xl text-[var(--ink)] max-w-4xl leading-[0.95]">
            India&apos;s mall ad spaces — <span className="font-serif-italic text-[var(--gold)]">indexed and live.</span>
          </h1>
          <p className="mt-5 text-[var(--slate-1)] text-base lg:text-lg max-w-2xl">
            Filter by city, mall, format, audience and budget. Click through to detailed specs,
            pricing and availability.
          </p>
        </div>
      </section>

      {/* Sticky filter bar */}
      <section className="sticky top-16 lg:top-20 z-30 glass-light border-b border-[var(--border)]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-4 flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[220px] relative">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--slate-2)]" />
            <input
              data-testid="market-search"
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              placeholder="Search by mall, city, format…"
              className="pl-10"
            />
          </div>
          <select data-testid="market-city-filter" value={filters.city} onChange={(e) => setFilters((f) => ({ ...f, city: e.target.value }))} className="min-w-[140px]">
            <option value="">All cities</option>
            {filterOpts.cities.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="market-format-filter" value={filters.format} onChange={(e) => setFilters((f) => ({ ...f, format: e.target.value }))} className="min-w-[170px]">
            <option value="">All formats</option>
            {filterOpts.formats.slice(0, 30).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="market-category-filter" value={filters.format_category} onChange={(e) => setFilters((f) => ({ ...f, format_category: e.target.value }))} className="min-w-[150px]">
            <option value="">All categories</option>
            {filterOpts.categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <button type="button" data-testid="market-more-filters" onClick={() => setShowFilters((v) => !v)} className="btn-secondary !py-2.5 !px-4 !text-[11px]">
            <Filter size={12} /> More
          </button>
          <select data-testid="market-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="min-w-[170px]">
            {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        {showFilters && (
          <div className="border-t border-[var(--border)] bg-[var(--bone)]">
            <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-4 flex flex-wrap items-end gap-5">
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--slate-2)] mb-1.5 font-semibold">Max price / day (₹)</div>
                <input
                  data-testid="market-max-price"
                  value={filters.max_price}
                  type="number"
                  onChange={(e) => setFilters((f) => ({ ...f, max_price: e.target.value }))}
                  placeholder="e.g. 5000"
                  className="min-w-[180px]"
                />
              </label>
              <label className="flex items-center gap-2 text-sm pb-2.5">
                <input type="checkbox" checked={filters.is_premium === true} onChange={(e) => setFilters((f) => ({ ...f, is_premium: e.target.checked ? true : undefined }))} className="!w-4 !h-4 mr-1" />
                Premium only
              </label>
              <button type="button" onClick={clearAll} className="text-xs text-[var(--gold)] hover:text-[var(--ink)] pb-2.5 uppercase tracking-[0.18em] font-semibold">
                <X size={11} className="inline -mt-0.5" /> Clear filters
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Results */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-10 lg:py-14">
        <div className="flex items-center justify-between mb-8">
          <div className="text-sm text-[var(--slate-1)]" data-testid="market-result-count">
            {loading ? "Loading…" : (
              <>
                <span className="font-serif text-2xl text-[var(--ink)]">{data.total.toLocaleString("en-IN")}</span>
                <span className="ml-2 text-[11px] uppercase tracking-[0.25em] text-[var(--slate-2)]">placements live</span>
              </>
            )}
          </div>
        </div>

        {loading ? (
          <SkeletonGrid />
        ) : sorted.length === 0 ? (
          <div className="text-center py-20 surface p-14" data-testid="market-empty">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--gold)]">No matches</div>
            <div className="mt-3 font-serif text-3xl">Try widening your search.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="market-grid">
            {sorted.map((item, idx) => <InventoryCard key={item.id} item={item} idx={idx} />)}
          </div>
        )}
      </section>
    </div>
  );
}

function InventoryCard({ item, idx }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay: Math.min(idx, 8) * 0.04 }}
    >
      <Link to={`/inventory/${item.id}`} data-testid={`inv-card-${item.id}`} className="block group">
        <div className="relative aspect-[4/5] overflow-hidden">
          <SmartImage src={item.photos?.[0]} format={item.format} alt={item.format} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 img-luxe" />
          <div className="absolute inset-0 hero-overlay" />
          <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
            <span className="chip bg-white/95 backdrop-blur">{item.format_category}</span>
            {item.is_premium && <span className="chip chip-gold chip-active">Premium</span>}
            {item.availability !== "Available" && (
              <span className={`chip ${item.availability === "Booked" ? "bg-[var(--crimson)] text-white border-[var(--crimson)]" : "bg-[var(--gold)] text-white border-[var(--gold)]"}`}>{item.availability}</span>
            )}
          </div>
          <div className="absolute bottom-3 right-3 flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase bg-white/95 px-2.5 py-1 text-[var(--ink)]">
            <Eye size={11} /> {item.visibility_score}
          </div>
          <div className="absolute bottom-4 left-4 right-12 text-white">
            <div className="font-serif text-xl leading-tight">{item.format}</div>
            <div className="mt-0.5 text-xs text-white/80 truncate">{item.mall_name}</div>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <div>
            <div className="font-medium text-sm text-[var(--ink)]">
              {formatINR(item.price_per_day)}<span className="text-[var(--slate-2)] font-normal text-xs"> / day</span>
            </div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--slate-2)] mt-0.5">
              <MapPin size={10} className="inline -mt-0.5" /> {item.city}
            </div>
          </div>
          <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--slate-2)] font-mono text-right">
            {formatCompactNumber(item.daily_footfall || item.monthly_footfall)}<br/>footfall
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="aspect-[4/5] bg-[var(--bone-warm)] animate-pulse" />
      ))}
    </div>
  );
}
